import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import { AuthGuard } from './Auth/AuthGuard';
import { useTokenMonitor } from '../hooks/useTokenMonitor';
import { AppShell } from './layout/AppShell';
import { HomePage } from './HomePage/HomePage';
import { ResearchHistory } from '../pages/ResearchHistory';
import ChatInterface from './ChatInterface';
import ResearchPage from '../pages/ResearchPage';
import TemplatesPage from '../pages/TemplatesPage';
import ImprovedTemplatesPage from '../pages/ImprovedTemplatesPage';
import CreateTemplatePage from '../pages/CreateTemplatePage';
import { CreateBlankTemplatePage } from '../pages/CreateBlankTemplatePage';
import { EditTemplatePage } from '../pages/EditTemplatePage';

type ResearchLength = 'quick_scan' | 'targeted' | 'deep_dive';

export const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const { apiKey, setApiKey } = useAppStore();
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // Monitor token expiration
  useTokenMonitor();

  const handleStartResearch = (message: string, providedApiKey?: string, researchLengthParam?: ResearchLength) => {
    // Update API key if provided
    if (providedApiKey) {
      setApiKey(providedApiKey);
    }
    
    // Navigate to research page with parameters
    navigate('/research', { 
      state: { 
        query: message, 
        apiKey: providedApiKey || apiKey, 
        researchLength: researchLengthParam || 'targeted' 
      } 
    });
  };

  const handleCreateTemplate = (message: string, providedApiKey?: string, researchLengthParam?: ResearchLength) => {
    // Update API key if provided
    if (providedApiKey) {
      setApiKey(providedApiKey);
    }
    
    // Navigate to create template page with parameters
    navigate('/templates/create', { 
      state: { 
        query: message, 
        apiKey: providedApiKey || apiKey, 
        researchLength: researchLengthParam || 'targeted' 
      } 
    });
  };

  return (
    <AuthGuard>
      <AppShell>
        <Routes>
          {/* Dashboard / Home - All routes use the same HomePage */}
          <Route 
            path="/" 
            element={
              <HomePage 
                onStartResearch={handleStartResearch}
                onCreateTemplate={handleCreateTemplate}
                currentApiKey={apiKey}
                onApiKeyUpdate={setApiKey}
                isDarkMode={isDarkMode}
                onToggleTheme={() => setIsDarkMode(!isDarkMode)}
              />
            } 
          />
          
          <Route 
            path="/home" 
            element={
              <HomePage 
                onStartResearch={handleStartResearch}
                onCreateTemplate={handleCreateTemplate}
                currentApiKey={apiKey}
                onApiKeyUpdate={setApiKey}
                isDarkMode={isDarkMode}
                onToggleTheme={() => setIsDarkMode(!isDarkMode)}
              />
            } 
          />
          
          {/* Research History */}
          <Route 
            path="/research-history" 
            element={<ResearchHistory />} 
          />
          
          {/* Active Research Session */}
          <Route 
            path="/research" 
            element={
              <ResearchPage 
                apiKey={apiKey}
                onApiKeyUpdate={setApiKey}
                isDarkMode={isDarkMode}
                onToggleTheme={() => setIsDarkMode(!isDarkMode)}
              />
            } 
          />
          
          {/* Templates */}
          <Route 
            path="/templates" 
            element={<ImprovedTemplatesPage apiKey={apiKey} isDarkMode={isDarkMode} onToggleTheme={() => setIsDarkMode(!isDarkMode)} />} 
          />
          <Route 
            path="/templates/create" 
            element={
              <CreateTemplatePage 
                apiKey={apiKey}
                isDarkMode={isDarkMode}
                onToggleTheme={() => setIsDarkMode(!isDarkMode)}
              />
            } 
          />
          <Route 
            path="/templates/create-blank" 
            element={<CreateBlankTemplatePage />} 
          />
          <Route 
            path="/templates/edit" 
            element={<EditTemplatePage />} 
          />
        </Routes>
      </AppShell>
    </AuthGuard>
  );
};